import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router';
import {
    Box,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography
} from '@mui/material';
import {
    ArrowBack,
    Assessment,
    CheckCircle,
    ChevronRight,
    FactCheck,
    Gavel,
    Groups,
    Label as LabelIcon,
    Link as LinkIcon,
    LockOpen,
    PersonSearch,
    Psychology,
    Sell as SellIcon,
    TrendingUp,
} from '@mui/icons-material';
import {useReportTasks} from '../../../../firebase/firestore/useReportTasks';
import {filterApplicableSuggestions} from '../../../../utils/suggestionApplicability';
import {useReportStore} from '../../useReportStore';
import {useLiveEditorDoc} from '../hooks/useLiveEditorDoc';
import {useLeftPanelSync} from '../../hooks';
import AIAssistantTaskView from './components/AIAssistantTaskView';
import IcrCoverageAnalysis from './components/IcrCoverageAnalysis';
import FindExpert from './components/findExpert';
import AlternativeHypotheses from './components/AlternativeHypotheses';
import ReferenceAnalysisView from './components/ReferenceAnalysisView';
import AboveUCitationsView from './components/AboveUCitationsView';
import AnalystPerspectives from './components/AnalystPerspectives';
import Forecast from './components/Forecast';
import {getForecastCount} from '../../../../utils/api/aiCitationAnalysis';
import {useAboveUCitations} from '../../../../hooks/useAboveUCitations';

const AI_ASSISTANT_VIEWS = [
    {
        key: 'icr_coverage',
        label: 'ICR Coverage Analysis',
        description: 'Analyze report coverage against ICR',
        icon: Assessment,
        isTaskBased: false,
    },
    {
        key: 'compliance_check',
        label: 'ICD-203',
        description: 'Check compliance with guidelines',
        icon: Gavel,
        isTaskBased: true,
    },
    {
        key: 'best_practices_check',
        label: 'Best practices',
        description: 'Check adherence to best practices',
        icon: CheckCircle,
        isTaskBased: true,
    },
    {
        key: 'alternative_hypotheses',
        label: 'Generate alternative hypotheses',
        description: 'Generate alternative explanations',
        icon: Psychology,
        isTaskBased: false,
    },
    {
        key: 'forecast',
        label: 'Forecast',
        description: 'Generate a forward-looking forecast, with optional persona opinions',
        icon: TrendingUp,
        isTaskBased: false,
    },
    {
        key: 'reference_analysis',
        label: 'Citation Analysis',
        description: 'Identify statements needing citations',
        icon: FactCheck,
        isTaskBased: false,
    },
    {
        key: 'above_u_citations',
        label: 'Find Low-side Alternatives',
        description: 'Suggest low-side alternatives for above-(U) citations',
        icon: LockOpen,
        isTaskBased: false,
    },
    {
        key: 'analyst_perspectives',
        label: 'Analyst Perspectives',
        description: 'Get synthetic analyst perspectives',
        icon: Groups,
        isTaskBased: false,
    },
    {
        key: 'find_expert',
        label: 'Find an expert',
        description: 'Get expert input on your report',
        icon: PersonSearch,
        isTaskBased: false,
    },
    {
        key: 'promote_product',
        label: 'Promote Your Product',
        description: 'Help your report be found by others',
        icon: SellIcon,
        isTaskBased: false,
        hasSubFeatures: true,
        subFeatures: [
            {
                key: 'entity_recognition',
                label: 'Link entities',
                description: 'Recognize and link entities in your report',
                icon: LinkIcon,
                isTaskBased: true,
            },
            {
                key: 'add_tags',
                label: 'Add tags',
                description: 'Add relevant tags to improve discoverability',
                icon: LabelIcon,
                isTaskBased: true,
            },
        ],
    },
];

const AIAssistant = ({isActive = false}) => {
    const {reportId} = useParams();
    const {aiView, setAiView} = useLeftPanelSync();


    const {data: allTasks, loading, error} = useReportTasks(reportId, {enabled: isActive});
    const editorView = useReportStore(s => s.editorView);
    const liveDoc = useLiveEditorDoc(editorView);

    // Count the same cards the section will show. ICD-203 and Best practices
    // rows stay in the database after the flagged sentence is gone; the badge
    // must not count those.
    const taskCounts = useMemo(() => {
        const visibleTasks = filterApplicableSuggestions(allTasks, liveDoc);
        const counts = {};
        AI_ASSISTANT_VIEWS.forEach(view => {
            if (view.isTaskBased) {
                counts[view.key] = visibleTasks.filter(t => t.taskType === view.key).length || 0;
            }
            // Count tasks for sub-features
            if (view.subFeatures) {
                view.subFeatures.forEach(subFeature => {
                    if (subFeature.isTaskBased) {
                        counts[subFeature.key] = visibleTasks.filter(t => t.taskType === subFeature.key).length || 0;
                    }
                });
            }
        });
        return counts;
    }, [allTasks, liveDoc]);

    const handleViewClick = useCallback((viewKey) => {
        console.log('🤖 AI Assistant view click:', {viewKey});
        setAiView(viewKey);
    }, [setAiView]);

    // Refetches whenever the main menu (badge) is showing again — i.e. on mount and every time
    // the user navigates back from a sub-view — so a forecast deleted in ForecastHistory.jsx is
    // reflected here instead of leaving this badge stuck at its stale pre-delete count.
    const [forecastCount, setForecastCount] = useState(null);
    useEffect(() => {
        if (!reportId || aiView) return;
        getForecastCount(reportId).then(setForecastCount);
    }, [reportId, aiView]);

    // MINSKY-17131: above-(U) citation count for the "Find (U) Alternatives" badge — computed
    // live from the Y.Doc (same hook the whole-document view itself uses), so it's always free
    // (no network round-trip) and never goes stale relative to what the view would show.
    const {citations: aboveUCitations} = useAboveUCitations();
    const aboveUCount = aboveUCitations.length;

    const handleBack = useCallback(() => {
        console.log('⬅️ AI Assistant back to main menu');
        setAiView(null);
    }, [setAiView]);

    const currentView = AI_ASSISTANT_VIEWS.find(v => v.key === aiView);
    const currentSubFeature = !currentView
        ? AI_ASSISTANT_VIEWS.flatMap(v => v.subFeatures || []).find(sf => sf.key === aiView)
        : null;

    // Show specific view if selected
    if (aiView && (currentView || currentSubFeature)) {
        const viewToDisplay = currentView || currentSubFeature;

        // If it's a parent feature with sub-features, show the sub-menu
        if (currentView?.hasSubFeatures) {
            return (
                <Stack bgcolor={'white'} flexGrow={1} overflow={'hidden'}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        px={1}
                        py={1}
                        bgcolor="grey.50"
                        borderBottom={1}
                        borderColor="divider"
                    >
                        <IconButton size="small" onClick={handleBack}>
                            <ArrowBack fontSize="small"/>
                        </IconButton>
                        <Typography variant="subtitle1" fontWeight="medium" ml={1}>
                            {currentView.label}
                        </Typography>
                    </Stack>
                    <List sx={{flex: 1, overflow: 'auto'}}>
                        {currentView.subFeatures.map((subFeature) => {
                            const IconComponent = subFeature.icon;
                            const count = taskCounts[subFeature.key];
                            const hasCount = subFeature.isTaskBased && typeof count === 'number';

                            return (
                                <ListItem key={subFeature.key} disablePadding>
                                    <ListItemButton onClick={() => handleViewClick(subFeature.key)}>
                                        <ListItemIcon>
                                            <IconComponent/>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" alignItems="center" gap={1}>
                                                    <span>{subFeature.label}</span>
                                                    {hasCount && count > 0 && (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                bgcolor: 'primary.main',
                                                                color: 'primary.contrastText',
                                                                borderRadius: '12px',
                                                                px: 1,
                                                                py: 0.25,
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold',
                                                                minWidth: 24,
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            {count}
                                                        </Box>
                                                    )}
                                                </Stack>
                                            }
                                            secondary={subFeature.description}
                                        />
                                        <ChevronRight color="action"/>
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Stack>
            );
        }

        // Otherwise show the actual feature view
        return (
            <Stack bgcolor={'white'} flexGrow={1} overflow={'hidden'} height={'100%'}>
                <Stack
                    direction="row"
                    alignItems="center"
                    px={1}
                    py={1}
                    bgcolor="grey.50"
                    borderBottom={1}
                    borderColor="divider"
                >
                    <IconButton size="small" onClick={handleBack}>
                        <ArrowBack fontSize="small"/>
                    </IconButton>
                    <Typography variant="subtitle1" fontWeight="medium" ml={1}>
                        {viewToDisplay.label}
                    </Typography>
                </Stack>
                <Stack overflow={'auto'} flexGrow={1}>
                    {aiView === 'icr_coverage' ? (
                        <IcrCoverageAnalysis/>
                    ) : aiView === 'find_expert' ? (
                        <FindExpert/>
                    ) : aiView === 'alternative_hypotheses' ? (
                        <AlternativeHypotheses/>
                    ) : aiView === 'forecast' ? (
                        <Forecast/>
                    ) : aiView === 'reference_analysis' ? (
                        <ReferenceAnalysisView viewConfig={viewToDisplay}/>
                    ) : aiView === 'above_u_citations' ? (
                        <AboveUCitationsView/>
                    ) : aiView === 'analyst_perspectives' ? (
                        <AnalystPerspectives/>
                    ) : (
                        <AIAssistantTaskView
                            viewKey={aiView}
                            viewConfig={viewToDisplay}
                            allTasks={allTasks}
                            loading={loading}
                            error={error}
                        />
                    )}
                </Stack>
            </Stack>
        );
    }

    // Show main menu
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={3}>
                <CircularProgress/>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Typography color="error">
                    Error loading tasks: {error.message}
                </Typography>
            </Box>
        );
    }

    return (
        <Stack height="100%" bgcolor={'white'}>
            <List sx={{flex: 1, overflow: 'auto'}}>
                {AI_ASSISTANT_VIEWS.map((view) => {
                    const IconComponent = view.icon;
                    const count = view.key === 'forecast'
                        ? forecastCount
                        : view.key === 'above_u_citations'
                            ? aboveUCount
                            : taskCounts[view.key];
                    const hasCount = view.key === 'forecast'
                        ? typeof count === 'number' && count > 0
                        : view.key === 'above_u_citations'
                            ? count > 0
                            : view.isTaskBased && typeof count === 'number' && count > 0;

                    return (
                        <ListItem key={view.key} disablePadding>
                            <ListItemButton onClick={() => handleViewClick(view.key)}>
                                <ListItemIcon>
                                    <IconComponent/>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            <span>{view.label}</span>
                                            {hasCount && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        bgcolor: 'primary.main',
                                                        color: 'primary.contrastText',
                                                        borderRadius: '12px',
                                                        px: 1,
                                                        py: 0.25,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        minWidth: 24,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {count}
                                                </Box>
                                            )}
                                        </Stack>
                                    }
                                    secondary={view.description}
                                />
                                <ChevronRight color="action"/>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Stack>
    );
};

export default AIAssistant;



